import React from 'react';
import {
  Dialog, Panel, Button, IconButton, Field, TextInput, Select, Chip, Banner, Tooltip, Icon,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import { useMemberReportTemplateEditor, TARGET_TYPE_LABELS, INSET_FIELDS } from './useMemberReportTemplateEditor.js';
import { MERGE_FIELDS, mergeFieldSpanHtml, defaultTemplateForType } from './memberReportMergeFields.js';
import { DESIGN_ELEMENTS, blankFieldSpanHtml, REPORT_BLANK_PRINT_CSS } from './memberReportBlanks.js';
import { letterPageCss } from './memberPrint.js';
import { Row } from '../../../next/ui';

/**
 * עיצוב דוח — תבנית ורקע מכתב, new design. Same state and actions (useMemberReportTemplateEditor) as the legacy
 * editor: templates list, A4 letterhead background (upload / remove, content insets with auto-save), template name /
 * type, merge fields and writing lines, the rich editor and the live A4 preview.
 */
const PREVIEW_CSS = `
  .letter-preview-shell .letter-page { min-height: 100% !important; height: 100%; page-break-after: auto;
    font-family: "David", "Arial Hebrew", Arial, sans-serif; direction: rtl; color: #111; font-size: 11pt; line-height: 1.55; }
  .letter-preview-shell .letter-page.has-blank .letter-body { min-height: 100% !important; }
`;

export default function MemberReportTemplateEditorNext(props) {
  const {
    open, onClose, templates, selectedId, name, setName, targetType, setTargetType, setBodyHtml, previewHtml, feedback, setFeedback, loading,
    blankMeta, blankPreviewUrl, letterInsets, insetsDirty, savingInsets, editorRef, fileRef, syncFromEditor, saveInsets, handleInsetChange,
    insertHtml, selectTemplate, handleNew, handleSave, handleDelete, handleBlankUpload, handleBlankRemove, hasBlank, previewPageClass, previewBgStyle,
  } = useMemberReportTemplateEditor(props);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="עיצוב דוח — תבנית ורקע מכתב"
      icon="edit"
      size="lg"
      className="nx-dialog-xl"
      footer={<Button variant="ghost" onClick={onClose}>סגור</Button>}
    >
      <div className="nx-stack">
        {feedback ? <Banner tone={feedback.includes('שגיאה') ? 'danger' : 'success'} icon={feedback.includes('שגיאה') ? 'alert' : 'check'} onClose={() => setFeedback('')}>{feedback}</Banner> : null}
        <Banner tone="info" icon="info">
          <b>רקע מכתב</b> — תמונת A4 מאחורי הטקסט (לוגו / כותרת). <b>תבנית</b> — הטקסט והשדות שמעל הרקע.{' '}
          <b>קווי כתיבה</b> — קווים לחתימה/הערות בתוך התבנית (לא חלק מהתמונה). <b>מרווחים</b> — הזיזו את התוכן עד שהוא יושב נכון מול הלוגו בתמונה.
        </Banner>

        <div className="nx-report-editor">
          <div className="nx-stack">
            <Panel title="תבניות" icon="list" flush actions={<Button size="sm" icon="plus" onClick={handleNew}>חדשה</Button>}>
              <div className="nx-tree" style={{ maxHeight: 170, overflow: 'auto' }}>
                {templates.map((t) => {
                  const on = String(t._id) === String(selectedId);
                  return (
                    <div key={t._id} className={`nx-tree__row is-click ${on ? 'is-on' : ''}`} role="button" tabIndex={0} onClick={() => selectTemplate(t)} onKeyDown={(e) => { if (e.key === 'Enter') selectTemplate(t); }}>
                      <span className="nx-grow"><b>{t.name}</b> <small className="ck-muted">{TARGET_TYPE_LABELS[t.targetType] || t.targetType}</small></span>
                      {on ? <IconButton size="sm" icon="trash" label="מחיקת תבנית" onClick={(e) => { e.stopPropagation(); handleDelete(); }} /> : null}
                    </div>
                  );
                })}
                {!templates.length ? <div className="nx-tree__row"><span className="ck-muted">אין תבניות עדיין</span></div> : null}
              </div>
            </Panel>

            <Panel title="רקע מכתב (תמונת A4)" sub="התמונה מופיעה כרקע לעמוד המכתב בהדפסה. כוונו מרווחים בתצוגה הגדולה." icon="image">
              <div className="nx-stack" style={{ gap: 8 }}>
                {hasBlank ? <img src={blankPreviewUrl} alt="רקע מכתב" className="nx-blank-thumb" /> : null}
                <small className="ck-muted">{blankMeta ? `פעיל: ${blankMeta.fileOriginalName || blankMeta.name}` : 'לא הוגדר רקע מכתב'}</small>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={handleBlankUpload} />
                <Row>
                  <Button size="sm" icon="upload" disabled={loading} onClick={() => fileRef.current?.click()}>העלה תמונה</Button>
                  {blankMeta ? <Button size="sm" variant="danger" icon="trash" disabled={loading} onClick={handleBlankRemove}>הסר</Button> : null}
                </Row>
                {blankMeta ? (
                  <>
                    <hr className="nx-hr" />
                    <Row justify="space-between">
                      <b>מרווחי תוכן (מ״מ)</b>
                      <small className="ck-muted">{savingInsets ? 'שומר…' : insetsDirty ? 'ממתין לשמירה…' : 'נשמר'}</small>
                    </Row>
                    <small className="ck-muted">הגדילו את העליון אם יש לוגו גדול בראש התמונה.</small>
                    {INSET_FIELDS.map((f) => (
                      <label key={f.key} className="nx-range">
                        <span className="nx-range__head"><span>{f.label}</span><span className="ck-muted">{`${letterInsets[f.key]} מ״מ`}</span></span>
                        <input type="range" min={0} max={f.max} step={1} value={letterInsets[f.key]} onChange={(e) => handleInsetChange(f.key, Number(e.target.value))} aria-label={`מרווח ${f.label}`} />
                      </label>
                    ))}
                    <Button size="sm" disabled={savingInsets || !insetsDirty} onClick={() => saveInsets(letterInsets)}>שמור מרווחים</Button>
                  </>
                ) : null}
              </div>
            </Panel>

            <Panel title="תבנית" icon="edit">
              <div className="nx-stack" style={{ gap: 10 }}>
                <Field label="שם תבנית"><TextInput value={name} onChange={(e) => setName(e.target.value)} /></Field>
                <Field label="סוג">
                  <Select
                    value={targetType}
                    onChange={(v) => { setTargetType(v); if (!selectedId) setBodyHtml(defaultTemplateForType(v)); }}
                    options={[{ value: 'list', label: 'רשימה' }, { value: 'breakdown', label: 'פילוח' }, { value: 'card', label: 'כרטיס נבחן' }]}
                  />
                </Field>
                <small className="ck-muted">שדות מיזוג</small>
                <Row gap={6}>{MERGE_FIELDS.map((f) => <Chip key={f.key} onClick={() => insertHtml(mergeFieldSpanHtml(f.key))}>{f.label}</Chip>)}</Row>
                <Row gap={4}>
                  <small className="ck-muted">קווי כתיבה / חתימה</small>
                  <Tooltip title="קווים ריקים בתוך התבנית למילוי ידני — לא חלק מתמונת הרקע"><span className="ck-muted"><Icon name="info" size={14} /></span></Tooltip>
                </Row>
                <Row gap={6}>{DESIGN_ELEMENTS.map((f) => <Chip key={f.key} tone="warning" onClick={() => insertHtml(blankFieldSpanHtml(f.key))}>{f.label}</Chip>)}</Row>
                <div
                  ref={editorRef}
                  className="nx-letter-editor"
                  contentEditable
                  suppressContentEditableWarning
                  onBlur={syncFromEditor}
                  onInput={syncFromEditor}
                  role="textbox"
                  aria-multiline="true"
                  aria-label="תוכן התבנית"
                />
                <Button variant="primary" icon="check" disabled={loading} onClick={handleSave}>שמור תבנית</Button>
              </div>
            </Panel>
          </div>

          <Panel title="תצוגה מקדימה" sub="כך ייצא בהדפסה (עמוד מכתב)" icon="eye" className="nx-letter-preview-panel">
            <div className="nx-letter-preview">
              <div className="nx-letter-page">
                <style>{letterPageCss(letterInsets)}</style>
                <style>{REPORT_BLANK_PRINT_CSS}</style>
                <style>{PREVIEW_CSS}</style>
                <div className="letter-preview-shell" style={{ height: '100%' }}>
                  <section className={previewPageClass} style={{ ...previewBgStyle, height: '100%', minHeight: '100%' }}>
                    {/* server-merged template preview (our own /preview-merge) */}
                    <div className="letter-body" dangerouslySetInnerHTML={{ __html: previewHtml || '<p style="color:#888">הקלידו תוכן בתבנית…</p>' }} />
                  </section>
                </div>
              </div>
            </div>
            {!hasBlank ? <small className="ck-muted" style={{ display: 'block', textAlign: 'center', marginTop: 8 }}>אין רקע מכתב — העלו תמונת A4 כדי לראות אותה כאן</small> : null}
          </Panel>
        </div>
      </div>
    </Dialog>
  );
}
